import { regex } from 'arkregex';

/**
 * Matches characters that need escaping when building dynamic regular expressions.
 * Mirrors the classic `escapeRegExp` implementation but powered by ArkRegex for consistency.
 */
export const REGEX_SPECIAL_CHARS_PATTERN = regex.as('[-/\\^$*+?.()|[\\]{}]', 'g');

export const escapeRegExp = (value: string): string => value.replaceAll(REGEX_SPECIAL_CHARS_PATTERN, '\\$&');

type ArkRegexFlags = `${'d' | ''}${'g' | ''}${'i' | ''}${'m' | ''}${'s' | ''}${'u' | 'v' | ''}${'y' | ''}`;
const normalizeFlags = (flags?: ArkRegexFlags): ArkRegexFlags | undefined =>
  flags && flags.length > 0 ? flags : undefined;

export const buildUnionPattern = (values: readonly string[], flags?: ArkRegexFlags): RegExp => {
  if (values.length === 0) {
    throw new Error('buildUnionPattern requires at least one value.');
  }
  const source = values.map((value) => escapeRegExp(value)).join('|');
  return regex.as(source, normalizeFlags(flags));
};

export const buildStartsWithPattern = (value: string, flags?: ArkRegexFlags): RegExp =>
  regex.as(`^${escapeRegExp(value)}`, normalizeFlags(flags));

export const NUMERIC_ID_PATTERN = regex.as('^\\d+$');
export const UUID_PATTERN = regex.as('^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$', 'i');

export const HANDLE_PREFIX_PATTERN = regex.as('^@+');
export const USERNAME_PATTERN = regex.as('^[A-Za-z0-9_]{1,15}$');
export const OPTIONAL_PREFIX_USERNAME_PATTERN = regex.as('^@?[A-Za-z0-9_]{1,15}$');
export const LONG_NUMERIC_ID_PATTERN = regex.as('^[0-9]{5,}$');
const EMBEDDED_LONG_NUMERIC_ID_PATTERN = regex.as('[0-9]{5,}');
export const HTTP_PROTOCOL_PATTERN = regex.as('^https?:', 'i');
export const HTTP_PROTOCOL_REPLACE_PATTERN = regex.as('^https?://', 'i');
export const URL_PREFIX_PATTERN = regex.as('^(?:https?:)?//', 'i');
export const SCRIPT_TAG_PATTERN = regex.as('<script[\\s\\S]*?<\\/script>', 'gi');
export const STYLE_TAG_PATTERN = regex.as('<style[\\s\\S]*?<\\/style>', 'gi');
export const HTML_COMMENT_PATTERN = regex.as('<!--([\\s\\S]*?)-->', 'g');
export const HTML_ANCHOR_CAPTURE_PATTERN = regex.as('<a\\s+[^>]*href="([^"]+)"[^>]*>(.*?)<\\/a>', 'gis');
export const MARKDOWN_LINK_CAPTURE_PATTERN = regex.as('\\[([^\\[\\]]+)\\]\\(([^)]+)\\)', 'g');
export const INLINE_HTML_EMPHASIS_TAG_PATTERN = regex.as('<\\/?(strong|em|b|i|u)>', 'gi');
export const HTML_BREAK_TAG_PATTERN = regex.as('<br\\s*\\/?>', 'gi');
export const HTML_CODE_TAG_PATTERN = regex.as('<\\/?code>', 'gi');
export const GENERIC_HTML_TAG_PATTERN = regex.as('<\\/?[^>]+>', 'g');
export const DOUBLE_LINE_BREAK_PATTERN = regex.as('\\n{2,}');
export const MARKDOWN_HEADING_CAPTURE_PATTERN = regex.as('^(#{1,6})\\s+(.*)$');
export const MARKDOWN_UNORDERED_LIST_ITEM_PATTERN = regex.as('^\\s*[-*+]\\s+');
export const MARKDOWN_ORDERED_LIST_ITEM_PATTERN = regex.as('^\\s*\\d+\\.\\s+');
export const LOCATION_COORDINATE_CAPTURE_PATTERN = regex.as(
  '\\(?\\s*([+-]?\\d{1,2}\\.\\d+)\\s*,\\s*([+-]?\\d{1,3}\\.\\d+)\\s*\\)?'
);
export const COMBINING_DIACRITIC_PATTERN = regex.as('[\\u0300-\\u036F]', 'g');
export const EMOJI_BLOCK_PATTERN = regex.as(
  '[\\u{1F300}-\\u{1F6FF}\\u{1F900}-\\u{1F9FF}\\u{1FA70}-\\u{1FAFF}\\u{2600}-\\u{27BF}\\u{1F1E6}-\\u{1F1FF}]',
  'gu'
);
export const EMOJI_VARIATION_SELECTOR_PATTERN = regex.as('[\\uFE0E\\uFE0F]', 'g');
export const LOCATION_SYMBOL_SEPARATOR_PATTERN = regex.as('[&|·•~]+', 'g');
export const LOCATION_CONNECTOR_WORD_PATTERN = regex.as('\\b(via|or|to)\\b', 'gi');
export const LOCATION_DASH_OR_SLASH_PATTERN = regex.as('[-/]+', 'g');
export const LOCATION_QUOTE_PATTERN = regex.as('["\'`]+', 'g');
export const COLLAPSED_WHITESPACE_PATTERN = regex.as('\\s+', 'g');
export const LOCATION_COMMA_SPACE_PATTERN = regex.as('\\s*,\\s*', 'g');
export const LOCATION_DUPLICATE_COMMA_PATTERN = regex.as(',+', 'g');
export const LOCATION_NON_ALPHANUMERIC_PATTERN = regex.as('[^a-z0-9,\\s]', 'g');
export const LOCATION_TRIM_DELIMITER_PATTERN = regex.as('^[,\\s]+|[,\\s]+$', 'g');
export const TS_DIAGNOSTIC_PATTERNS: readonly RegExp[] = [
  regex.as('^(?<file>.+)\\((?<line>\\d+),(?<column>\\d+)\\): error TS(?<code>\\d+): (?<message>.+)$', 'i'),
  regex.as('^(?<file>.+):(?<line>\\d+):(?<column>\\d+) - error TS(?<code>\\d+): (?<message>.+)$', 'i'),
  regex.as('^(?<file>.+):(?<line>\\d+):(?<column>\\d+): error TS(?<code>\\d+): (?<message>.+)$', 'i'),
] as const;
export const TS_FOUND_ERRORS_PATTERN: RegExp = regex.as('Found (\\d+) errors?', 'i');
export const TS_FILE_CHANGE_PATTERN: RegExp = regex.as('File change detected', 'i');
export const TS_COMPILATION_START_PATTERN: RegExp = regex.as('Starting compilation', 'i');
export const TS_BUILD_FINISHED_PATTERN: RegExp = regex.as('^build finished', 'i');
export const TS_ERROR_CODE_PATTERN: RegExp = regex.as('error TS\\d+', 'i');
export const ESLINT_DIAGNOSTIC_PATTERN: RegExp = regex.as(
  '^(?<file>.+?):(?<line>\\d+):(?<column>\\d+): (?<severity>error|warning) (?<message>.+?)(?: \\[(?<rule>[^\\]]+)\\])?$',
  'i'
);
export const ESLINT_SUMMARY_PATTERN: RegExp = regex.as(
  '✖ (?<total>\\d+) problems? \\((?<errors>\\d+) errors?, (?<warnings>\\d+) warnings?\\)',
  'i'
);
export const ESLINT_NO_PROBLEMS_PATTERN: RegExp = regex.as('✔ (?:No problems|Clean)', 'i');
export const ESLINT_NO_PROBLEMS_FOUND_PATTERN: RegExp = regex.as('No problems found', 'i');
export const ESLINT_RECOMPILE_PATTERN: RegExp = regex.as('(File\\(s\\) changed|Linting)', 'i');
export const ESLINT_WATCHING_PATTERN: RegExp = regex.as('Watching for file changes', 'i');
export const ESLINT_WATCH_UNSUPPORTED_PATTERN: RegExp = regex.as("Invalid option '--watch'", 'i');
export const ESLINT_GENERIC_ERROR_PATTERN: RegExp = regex.as('error', 'i');
export const RELATIVE_PROFILE_PATH_PATTERN = regex.as('^/@?([A-Za-z0-9_]{1,15})(?:[/?#]|$)');
export const BARE_HANDLE_PATTERN = regex.as('^@([A-Za-z0-9_]{1,15})$');
export const HANDLE_ONLY_TOKEN_PATTERN = regex.as('^T[0-9A-Z]+$');
export const INLINE_MENTION_CAPTURE_PATTERN = regex.as('(?<![A-Za-z0-9_@])@([A-Za-z0-9_]{1,15})(?![A-Za-z0-9_@])', 'g');
export const MENTION_REGEX = regex.as('@(?<username>\\w+)', 'g');
export const EXTENDED_TWEET_ID_PATTERN = regex.as('^[0-9_]{5,}$');

export const ID_SEQUENCE_PATTERN = regex.as('\\d{5,}');
export const TWEET_SCHEME_PATTERN = regex.as('^tweet:\\s*(\\d[\\d_]*)$', 'i');
export const LEADING_NUMBER_PREFIX_PATTERN = regex.as('^\\d+\\s*');
export const PROFILE_IMAGE_SUFFIX_PATTERN: RegExp = regex.as(
  '_(mini|normal|bigger|\\d{2,4}x\\d{2,4})(\\.[a-zA-Z0-9]+)(\\?.*)?$',
  ''
);
export const PROFILE_IMAGE_PATH_PATTERN: RegExp = regex.as(
  '(\\/profile_images\\/[^/]+\\/[A-Za-z0-9_-]+)(\\.[a-zA-Z0-9]+)(\\?.*)?$',
  ''
);

export const QUOTE_TRIM_PATTERN = regex.as('^[\'" ]|[\'" ]$', 'g');
export const NAMESPACE_PATTERN = regex.as('^[A-Za-z0-9_-]+$');
export const BOOLEAN_LIKE_PATTERN = regex.as('^(0|1|true|false|yes|no|on|off)$', 'i');
export const SAFE_PROFILE_KEY_PATTERN = regex.as('^[\\w:@.-]+$');
export const ANSI_ESCAPE_PATTERN: RegExp = regex.as('\\u001B\\[[0-9;]*m', 'g');
export const API_KEY_PATTERN = regex.as('^[A-Za-z0-9_.-]+$');
export const TWITTER_ID_PATTERN = regex.as('^\\d+$');
export const HEX_CHAR_PATTERN = regex.as('^[a-fA-F0-9]+$');
export const BEARER_TOKEN_CHAR_PATTERN = regex.as('^[a-zA-Z0-9%_-]+$');
export const TURBOPUFFER_KEY_PATTERN = regex.as('^tpuf_[a-zA-Z0-9]+$');
export const NEWLINE_PATTERN = regex.as('\\n');
export const buildLangfuseKeyPattern = (prefix: string): RegExp => regex.as(`^${escapeRegExp(prefix)}[a-zA-Z0-9-]+$`);
export const WHITESPACE_SPLIT_PATTERN = regex.as('\\s+');
export const AUTH_CALLBACK_PATH_PATTERN = regex.as('^/.+$');
export const POSTHOG_KEY_PATTERN = regex.as('^phc_[a-zA-Z0-9]{40,}$');
export const INLINE_HANDLE_WITH_TIGHT_COLON_PATTERN = regex.as('(@[A-Za-z0-9_]{1,15})(:)(?=\\S)', 'g');
export const RICH_CONTENT_PATTERN = regex.as('<(tweet|user):([^>]+)>', 'gi');
export const JSON_PATH_IDENTIFIER_PATTERN = regex.as('^[A-Za-z_][A-Za-z0-9_]*$');
export const FRAME_WITH_FUNCTION_PATTERN = regex.as('at\\s+(?:async\\s+)?(\\S+)?\\s+((.+?):(\\d+):(\\d+))');
export const FRAME_WITH_FILE_PATTERN = regex.as('at\\s+(.*?):(\\d+):(\\d+)');
export const ASYNC_PREFIX_PATTERN = regex.as('^async\\s+', 'i');
export const SIGN_IN_AGAIN_PATTERN = regex.as('sign in again', 'i');
export const INLINE_TWEET_OPEN_TWEET_ON_X_PATTERN = regex.as('open tweet on x', 'i');
// When Next.js emits chunk filenames we get hashes and language hints. Reuse these patterns
// so all callers understand which suffixes and hashes to strip before normalizing paths.
export const CHUNK_HASH_PATTERN = regex.as('_[0-9a-f]{6,}$', 'i');
export const CHUNK_LANG_SUFFIX_PATTERN = regex.as('_(ts|tsx|js|jsx|mjs|cjs)$', 'i');
export const FILE_EXTENSION_PATTERN = regex.as('\\.(?:m?js|cjs)$', 'i');
export const HTTP_METHOD_PATTERN = regex.as('^(DELETE|GET|HEAD|OPTIONS|PATCH|POST|PUT)\\s+', 'i');
export const VERSION_PREFIX_PATTERN = regex.as('^v\\d+/', 'i');
export const LEADING_SLASH_PATTERN = regex.as('^/+', 'g');
export const buildAbsoluteUrlPattern = (relativePath: string): RegExp =>
  // Match absolute URLs that end with a canonicalized relative path plus optional query string.
  regex.as(`https?://[^\\s]+${escapeRegExp(relativePath)}(?:\\?.*)?$`);

export const USER_HANDLE_PART_SOURCE = '[A-Za-z0-9_]{1,15}';
export const BOUNDARY_CHARS_SOURCE = '[([{“”\'"-]';
export const TRAILING_BRACKET_PATTERN = regex.as('\\]$');
export const LEADING_PUNCTUATION_PATTERN = regex.as('^[\\s([{“”\'"-]+');
export const TRAILING_SLASH_PATTERN = regex.as('/+$');
export const ALPHANUMERIC_CHAR_PATTERN = regex.as('[A-Za-z0-9_]');
export const LETTER_PATTERN = regex.as('[a-z]', 'i');
export const ANALYSIS_ACTIONS_MENU_PATTERN = regex.as('analysis actions', 'i');
export const STREAMING_TOKENS_LAUNCH_PATTERN = regex.as('launch', 'i');
export const STREAMING_TOKENS_FRAMEWORK_MILESTONE_PATTERN = regex.as('framework milestone', 'i');
export const FREQUENCY_TABLE_SORT_ASCENDING_PATTERN = regex.as('sort ascending', 'i');
export const ANALYSIS_CONTENT_METADATA_TRIGGER_PATTERN = regex.as('2 tweets in .*2 days', 'i');
export const ANALYSIS_CONTENT_TWEETS_FETCHED_PATTERN = regex.as('tweets fetched', 'i');
export const MEDIA_VIEWER_PREVIOUS_PATTERN = regex.as('Previous', 'i');
export const MEDIA_VIEWER_NEXT_PATTERN = regex.as('Next', 'i');
export const REFRESH_FROM_TWITTER_PATTERN = regex.as('refresh from twitter', 'i');
export const EXPANDABLE_TWEET_SHOW_MORE_PATTERN = regex.as('show more', 'i');
export const USER_PROFILE_FOLLOW_PATTERN = regex.as('follow', 'i');
export const USER_PROFILE_MORE_OPTIONS_PATTERN = regex.as('more options', 'i');
export const USER_PROFILE_FETCH_FULL_TIMELINE_PATTERN = regex.as('fetch full timeline', 'i');
export const USER_PROFILE_VIEW_PULSE_PATTERN = regex.as('view pulse', 'i');
export const UNAUTHORIZED_PATTERN = regex.as('unauthorized', 'i');

export const TWITTER_THREAD_API_PATTERN = regex.as('^https://api.twitter.com/2/tweets/[^/]+/thread$', 'i');
export const HTTP_URL_PATTERN = regex.as('^https?://.+', 'i');
export const URL_SCHEME_PREFIX_PATTERN = regex.as('^[a-zA-Z][a-zA-Z0-9+.-]*://');
export const EMAIL_PATTERN = regex.as('^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$', 'i');
export const TWITTER_TWEET_ID_PATTERN = regex.as('^\\d{19}$');
export const BEARER_AUTH_PATTERN = regex.as('^Bearer .+$');
export const TWEET_ID_WITH_SEPARATORS_PATTERN: RegExp = regex.as('^\\d+(?:_\\d+)*$');
export const APP_RELATIVE_TWEET_PATTERN: RegExp = regex.as('^/tweet/\\d+(?:_\\d+)*(?:/|$)');
export const RELATIVE_TWEET_CAPTURE_PATTERN: RegExp = regex.as('^/tweet/(\\d+(?:_\\d+)*)(?:/|$)');
export const TWITTER_HOST_PATTERN: RegExp = regex.as('^twitter\\.com$', 'i');
export const X_HOST_PATTERN: RegExp = regex.as('^x\\.com$', 'i');
export const TWEET_STATUS_WITH_USER_PATTERN: RegExp = regex.as(
  '^https?:\\/\\/(?:www\\.|mobile\\.)?(?:twitter\\.com|x\\.com)\\/[^/]+\\/status\\/\\d+(?:_\\d+)*',
  'i'
);
export const TWEET_STATUS_IWEB_PATTERN: RegExp = regex.as(
  '^https?:\\/\\/(?:www\\.|mobile\\.)?(?:twitter\\.com|x\\.com)\\/i\\/(?:web\\/)?status\\/\\d+(?:_\\d+)*',
  'i'
);
export const LEADING_NUMERIC_CAPTURE_PATTERN: RegExp = regex.as('^(\\d+(?:_\\d+)*)');

export const WWW_PREFIX_PATTERN: RegExp = regex.as('^www.', 'i');
export const MOBILE_PREFIX_PATTERN: RegExp = regex.as('^mobile.', 'i');
export const MOBILE_USER_AGENT_PATTERN = regex.as(
  'Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone|IEMobile|Opera Mini|Mobile',
  'i'
);

export const stripHandlePrefixes = (value: string): string => value.replace(HANDLE_PREFIX_PATTERN, '');

export const isValidUsernameShape = (value: string): boolean => USERNAME_PATTERN.test(value);

export const extractLongNumericId = (value: string): string | null => {
  const match = value.match(EMBEDDED_LONG_NUMERIC_ID_PATTERN);
  return match ? match[0] : null;
};

export const extractRelativeHandle = (value: string): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const directMatch = trimmed.match(RELATIVE_PROFILE_PATH_PATTERN);
  if (directMatch?.[1]) {
    return directMatch[1];
  }
  const bareMatch = trimmed.match(BARE_HANDLE_PATTERN);
  return bareMatch?.[1] ?? null;
};
